import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LuSparkles, LuUser, LuSend, LuLoaderCircle } from 'react-icons/lu';
import { sendOllamaChatStream } from '../../api/ollama';
import type { Document } from '../../types';

const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  a: ({ href, children, ...props }) => {
    const safe = href && (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('/'));
    if (safe) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:opacity-80" {...props}>
          {children}
        </a>
      );
    }
    return <span {...props}>{children}</span>;
  },
  ul: ({ children, ...props }) => <ul className="my-1 list-disc pl-4 space-y-0.5 text-xs" {...props}>{children}</ul>,
  ol: ({ children, ...props }) => <ol className="my-1 list-decimal pl-4 space-y-0.5 text-xs" {...props}>{children}</ol>,
  li: ({ children, ...props }) => <li className="leading-relaxed" {...props}>{children}</li>,
  p: ({ children, ...props }) => <p className="my-1 first:mt-0 last:mb-0 leading-relaxed text-xs" {...props}>{children}</p>,
  strong: ({ children, ...props }) => <strong className="font-semibold" {...props}>{children}</strong>,
  em: ({ children, ...props }) => <em className="italic" {...props}>{children}</em>,
  code: ({ className, children, ...props }) =>
    className ? (
      <code className="block text-xs" {...props}>{children}</code>
    ) : (
      <code className="rounded bg-gray-100 dark:bg-stone-700 px-1 py-0.5 text-xs" {...props}>{children}</code>
    ),
  pre: ({ children, ...props }) => <pre className="my-1 overflow-x-auto rounded bg-gray-100 dark:bg-stone-700 p-1.5 text-xs" {...props}>{children}</pre>,
  blockquote: ({ children, ...props }) => <blockquote className="my-1 border-l-2 border-primary/50 pl-2 italic text-gray-600 dark:text-slate-400 text-xs" {...props}>{children}</blockquote>,
};

type IconProps = { className?: string; 'aria-hidden'?: boolean };
const SparkIcon = LuSparkles as React.ComponentType<IconProps>;
const UserIcon = LuUser as React.ComponentType<IconProps>;
const SendIcon = LuSend as React.ComponentType<IconProps>;
const LoadIcon = LuLoaderCircle as React.ComponentType<IconProps>;

type MessageRole = 'user' | 'assistant';
interface ChatMessage {
  role: MessageRole;
  content: string;
}

function buildDocumentContext(doc: Document, documentContent: string | null): string {
  const hasText = Boolean(documentContent && documentContent.trim());
  const meta = [
    'You are ClickCompanion, the AI assistant inside ClickLaw.',
    'The user has this document OPEN in the viewer right now. You have access to it below.',
    hasText
      ? 'The full document text is provided below. You MUST answer questions using this text. Do NOT ask the user to provide, upload, or share the file—they already have it open.'
      : 'Document metadata only (no extracted text for this file type). Answer from the metadata; do not ask the user to provide the file.',
    '',
    'Document:',
    `- Name: ${doc.name}`,
    `- Type: ${doc.type}`,
    `- Case: ${doc.caseName ?? '—'}`,
    `- Uploaded by: ${doc.uploadedBy?.username ?? '—'}`,
    `- Size: ${doc.size ?? '—'}`,
  ];
  const parts = [meta.join('\n')];
  if (hasText) {
    parts.push('');
    parts.push('--- FULL DOCUMENT TEXT (answer from this) ---');
    parts.push(documentContent!.trim());
    parts.push('--- END DOCUMENT TEXT ---');
  }
  return parts.join('\n');
}

function getInitialMessage(hasContent: boolean): string {
  return hasContent
    ? 'Ask me anything about this document. I can answer using both its details and the actual text content.'
    : 'Ask me anything about this document. I can answer from its details (name, type, case, who uploaded it, when, size). Document text was not available for this file.';
}

export interface DocumentChatPanelProps {
  document: Document;
  /** Extracted text from the document (e.g. from PDF). When provided, the AI can answer from actual content. */
  documentContent?: string | null;
  className?: string;
}

export default function DocumentChatPanel({ document: doc, documentContent = null, className = '' }: DocumentChatPanelProps) {
  const hasContent = Boolean(documentContent && documentContent.trim());
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    { role: 'assistant', content: getInitialMessage(hasContent) },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const context = useMemo(() => buildDocumentContext(doc, documentContent), [doc, documentContent]);

  // When document content becomes available, update the greeting
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0].role !== 'assistant') return prev;
      const newMsg = getInitialMessage(hasContent);
      return prev[0].content === newMsg ? prev : [{ role: 'assistant', content: newMsg }];
    });
  }, [hasContent]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || isLoading) return;

    setMessages((prev) => [...prev, { role: 'user', content }]);
    setInput('');
    setError(null);
    setIsLoading(true);

    const apiMessages = [
      ...messages.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content },
    ];

    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      await sendOllamaChatStream(apiMessages, context, (token) => {
        setMessages((prev) => {
          const n = [...prev];
          const last = n[n.length - 1];
          if (last?.role === 'assistant') {
            n[n.length - 1] = { ...last, content: last.content + token };
          }
          return n;
        });
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
      setMessages((prev) => {
        const n = [...prev];
        const last = n[n.length - 1];
        if (last?.role === 'assistant' && !last.content) {
          n[n.length - 1] = { ...last, content: 'Sorry, something went wrong. Please try again.' };
        }
        return n;
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex flex-col min-h-0 flex-1 ${className}`}>
      <div
        ref={scrollRef}
        className="flex-1 min-h-[100px] overflow-y-auto space-y-2 pr-1 clicklaw-scrollbar"
      >
        {messages.map((m, i) => {
          const isLast = i === messages.length - 1;
          const showStreaming = isLast && m.role === 'assistant' && !m.content && isLoading;
          const displayContent = m.content || (showStreaming ? '...' : '');
          return (
            <div key={i} className="flex gap-2 text-xs">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                {m.role === 'assistant' ? (
                  showStreaming ? (
                    <LoadIcon className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <SparkIcon className="h-3.5 w-3.5" aria-hidden />
                  )
                ) : (
                  <UserIcon className="h-3.5 w-3.5" aria-hidden />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {m.role === 'assistant' ? 'AI' : 'You'}
                </span>
                <div className="chat-message-body text-slate-600 dark:text-slate-300 [&>*:first-child]:mt-0.5 [&>*:last-child]:mb-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {displayContent}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          );
        })}
        {error && (
          <p className="text-xs text-red-500 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </div>

      <footer className="shrink-0 pt-3 mt-2 -mx-4 -mb-4 px-4 pb-4">
        <form onSubmit={handleSubmit} className="flex gap-1.5">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about this document..."
            disabled={isLoading}
            className="min-w-0 flex-1 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2.5 py-1.5 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="shrink-0 rounded-md bg-primary p-1.5 text-white hover:bg-primary-700 disabled:opacity-50 disabled:pointer-events-none"
            aria-label="Send"
          >
            <SendIcon className="h-4 w-4" aria-hidden />
          </button>
        </form>
      </footer>
    </div>
  );
}
